<script lang="ts">
  import { gallery } from '../lib/gallery.svelte';
  import { imageUrl, viewportImageWidth, markResizeUnavailable } from '../lib/image';

  // Only the current photo and a couple of neighbours hold real <img> srcs, so
  // a 200-photo folder never mounts 200 network requests. The rest are sized
  // placeholders that keep the scroll geometry correct.
  const WINDOW = 2;

  let container = $state<HTMLDivElement | null>(null);
  let width = $state(viewportImageWidth());

  // Guards a programmatic scroll so the settle handler doesn't treat our own
  // scrollTo as a user gesture and fight it.
  let programmatic = false;
  let settleTimer: ReturnType<typeof setTimeout> | undefined;
  let unlockTimer: ReturnType<typeof setTimeout> | undefined;
  let wheelLock = false;
  let mounted = false;
  // True when the current index change came from a native (touch) scroll, so the
  // effect knows we're already positioned and shouldn't animate again.
  let drivenByScroll = false;

  // One-step directional slide for far jumps (e.g. clicking a distant photo in
  // the listing): we animate between just the two photos instead of scrolling
  // through all the ones in between.
  type Slide = { outSrc: string; inSrc: string; dir: 'fwd' | 'back'; active: boolean };
  let slide = $state<Slide | null>(null);
  let slideTimer: ReturnType<typeof setTimeout> | undefined;

  function inWindow(i: number): boolean {
    return Math.abs(i - gallery.index) <= WINDOW;
  }

  function scrollToIndex(i: number, smooth: boolean): void {
    if (!container || i < 0) return;
    const target = i * container.clientHeight;
    if (Math.abs(container.scrollTop - target) < 2) return;
    programmatic = true;
    clearTimeout(unlockTimer);
    container.scrollTo({ top: target, behavior: smooth ? 'smooth' : 'auto' });
    unlockTimer = setTimeout(() => (programmatic = false), smooth ? 600 : 50);
  }

  // Slide between two photos over a single screen-height, regardless of how far
  // apart they are. The real scroll jumps instantly behind the overlay.
  function startSlide(fromIndex: number, toIndex: number): void {
    const out = gallery.photos[fromIndex];
    const inc = gallery.photos[toIndex];
    if (!out || !inc) {
      scrollToIndex(toIndex, false);
      return;
    }
    scrollToIndex(toIndex, false); // position the real viewer at the target, hidden
    slide = {
      outSrc: imageUrl(out.filename, width),
      inSrc: imageUrl(inc.filename, width),
      dir: toIndex > fromIndex ? 'fwd' : 'back',
      active: false,
    };
    // Flip to the active transforms next frame so the transition animates.
    requestAnimationFrame(() =>
      requestAnimationFrame(() => {
        if (slide) slide = { ...slide, active: true };
      }),
    );
    clearTimeout(slideTimer);
    slideTimer = setTimeout(() => (slide = null), 560);
  }

  // Determine the snapped photo after scrolling settles (touch / native snap).
  function settle(): void {
    if (programmatic || !container) return;
    const i = Math.round(container.scrollTop / container.clientHeight);
    const photo = gallery.photos[i];
    if (photo && photo.id !== gallery.photoId) {
      drivenByScroll = true; // native scroll already moved us — don't re-animate
      gallery.selectPhoto(photo.id);
    }
  }

  function onScroll(): void {
    if (programmatic) return;
    gallery.dismissHint(); // a real (touch/trackpad) scroll counts as interaction
    clearTimeout(settleTimer);
    settleTimer = setTimeout(settle, 90);
  }

  // Desktop: one photo per wheel gesture, with a cooldown to swallow momentum.
  // Scrolling forward past the last photo opens the menu.
  function onWheel(e: WheelEvent): void {
    e.preventDefault();
    if (wheelLock || Math.abs(e.deltaY) < 4) return;
    gallery.dismissHint();
    wheelLock = true;
    if (e.deltaY > 0) gallery.forward();
    else gallery.step(-1);
    setTimeout(() => (wheelLock = false), 700);
  }

  // Touch: a deliberate upward swipe while already at the last photo opens the
  // menu (the equivalent of "scrolling past the end").
  let touchStartY: number | null = null;
  let startedAtBottom = false;

  function onTouchStart(e: TouchEvent): void {
    touchStartY = e.touches[0]?.clientY ?? null;
    startedAtBottom =
      !!container && container.scrollTop + container.clientHeight >= container.scrollHeight - 2;
  }

  function onTouchEnd(e: TouchEvent): void {
    if (touchStartY === null) return;
    const dy = touchStartY - (e.changedTouches[0]?.clientY ?? touchStartY);
    if (startedAtBottom && dy > 60) gallery.openPanel();
    touchStartY = null;
  }

  function onClick(): void {
    if (gallery.panelOpen) gallery.closePanel();
  }

  // If a resized URL fails (e.g. Image Transformations isn't enabled on this
  // host — like *.pages.dev), fall back to the original image and stop using
  // the resize prefix for subsequent images.
  function onImgError(e: Event): void {
    const img = e.currentTarget as HTMLImageElement;
    if (img.dataset.fellBack) return;
    const i = img.src.indexOf('/cdn/');
    if (i < 0 || !img.src.includes('/cdn-cgi/image/')) return;
    markResizeUnavailable();
    img.dataset.fellBack = '1';
    img.src = img.src.slice(i); // strip the /cdn-cgi/image/... prefix
  }

  // Keep the scroll position aligned with the current photo whenever it changes
  // from anywhere (panel click, keyboard, route/back-forward). Single-step moves
  // animate; larger jumps (e.g. clicking far down the listing) cut instantly
  // rather than scrolling through every photo in between.
  let lastIndex = -1;
  let lastFolder = '';
  $effect(() => {
    // Track the photo list too, so switching folders always re-runs this even
    // when the new index happens to match the old one.
    void gallery.photos;
    const i = gallery.index;
    const folderChanged = gallery.folderPath !== lastFolder;
    if (container && i >= 0) {
      if (drivenByScroll) {
        // Touch scroll already positioned us — leave it.
      } else if (!mounted || folderChanged) {
        // Initial load / folder switch: cut instantly to the target.
        scrollToIndex(i, false);
      } else if (i !== lastIndex) {
        // Every programmatic move (adjacent or far: list click, wheel, keyboard)
        // uses the same one-screen directional slide, so they all feel identical.
        startSlide(lastIndex, i);
      }
    }
    drivenByScroll = false;
    lastIndex = i;
    lastFolder = gallery.folderPath;
  });

  $effect(() => {
    if (!container) return;
    mounted = true;

    container.addEventListener('scroll', onScroll, { passive: true });
    container.addEventListener('scrollend', settle);
    container.addEventListener('wheel', onWheel, { passive: false });
    container.addEventListener('touchstart', onTouchStart, { passive: true });
    container.addEventListener('touchend', onTouchEnd, { passive: true });

    const onResize = () => {
      width = viewportImageWidth();
      scrollToIndex(gallery.index, false);
    };
    window.addEventListener('resize', onResize);

    return () => {
      container?.removeEventListener('scroll', onScroll);
      container?.removeEventListener('scrollend', settle);
      container?.removeEventListener('wheel', onWheel);
      container?.removeEventListener('touchstart', onTouchStart);
      container?.removeEventListener('touchend', onTouchEnd);
      window.removeEventListener('resize', onResize);
      clearTimeout(settleTimer);
      clearTimeout(unlockTimer);
      clearTimeout(slideTimer);
    };
  });
</script>

<div class="viewer" bind:this={container} onclick={onClick} role="presentation">
  {#each gallery.photos as photo, i (photo.id)}
    <section class="slide" aria-roledescription="photo" aria-label={photo.title}>
      {#if inWindow(i)}
        <img
          src={imageUrl(photo.filename, width)}
          alt={photo.title}
          draggable="false"
          fetchpriority={i === gallery.index ? 'high' : 'auto'}
          loading={i === gallery.index ? 'eager' : 'lazy'}
          decoding="async"
          onerror={onImgError}
        />
      {/if}
    </section>
  {/each}
</div>

{#if slide}
  <div class="slide-anim {slide.dir}" class:active={slide.active} aria-hidden="true">
    <img class="layer out" src={slide.outSrc} alt="" draggable="false" />
    <img class="layer in" src={slide.inSrc} alt="" draggable="false" />
  </div>
{/if}

<style>
  .viewer {
    position: absolute;
    inset: 0;
    height: 100%;
    width: 100%;
    overflow-y: auto;
    overflow-x: hidden;
    scroll-snap-type: y mandatory;
    /* Don't let the browser keep the previously-visible photo in view when the
       photo list changes (folder switch) — we control the scroll position. */
    overflow-anchor: none;
    scrollbar-width: none;
    -ms-overflow-style: none;
    background: var(--bg);
  }
  .viewer::-webkit-scrollbar {
    display: none;
  }

  /* Directional-slide overlay for far jumps. Sits above the viewer but below the
     menu (z 10/20), so during a listing click it slides behind the open drawer. */
  .slide-anim {
    position: fixed;
    inset: 0;
    z-index: 1;
    overflow: hidden;
    background: var(--bg);
    pointer-events: none;
  }
  .slide-anim .layer {
    position: absolute;
    inset: 0;
    width: 100%;
    height: 100%;
    object-fit: contain;
    -webkit-user-drag: none;
    user-select: none;
  }
  .slide-anim.active .layer {
    /* ease-in-out, like a native smooth scroll (gentle start and end). */
    transition: transform 0.5s cubic-bezier(0.4, 0, 0.2, 1);
  }
  /* Forward (target is later): incoming rises from the bottom, outgoing exits top. */
  .slide-anim.fwd .out {
    transform: translateY(0);
  }
  .slide-anim.fwd .in {
    transform: translateY(100%);
  }
  .slide-anim.fwd.active .out {
    transform: translateY(-100%);
  }
  .slide-anim.fwd.active .in {
    transform: translateY(0);
  }
  /* Back (target is earlier): incoming drops from the top, outgoing exits bottom. */
  .slide-anim.back .out {
    transform: translateY(0);
  }
  .slide-anim.back .in {
    transform: translateY(-100%);
  }
  .slide-anim.back.active .out {
    transform: translateY(100%);
  }
  .slide-anim.back.active .in {
    transform: translateY(0);
  }

  .slide {
    position: relative;
    height: 100%;
    width: 100%;
    scroll-snap-align: start;
    scroll-snap-stop: always;
    display: flex;
    align-items: center;
    justify-content: center;
  }

  img {
    /* Fill the viewport box and contain — scales images up so they always touch
       top/bottom or left/right (matches the slide overlay; no snap afterwards). */
    width: 100%;
    height: 100%;
    object-fit: contain;
    /* Deterrents against casual saving: no selection, no drag-to-desktop, no
       iOS long-press "Save Image" menu, and no direct pointer target (taps and
       swipes pass through to the viewer's own handlers). */
    user-select: none;
    -webkit-user-select: none;
    -webkit-user-drag: none;
    -webkit-touch-callout: none;
    pointer-events: none;
  }
</style>
